import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  Badge,
  Box,
  Button,
  Divider,
  DownloadIcon,
  EditIcon,
  ExternalLinkIcon,
  FileUpload,
  Heading,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  RayIcon,
  Text,
  TrashIcon,
  useToast,
} from '@razorpay/blade/components';
import type { BladeFile, BladeFileList } from '@razorpay/blade/components';
import { MimeType } from 'components/ui/FileUpload/constants';
import LoanCoins from '../../../../assets/loan/loans-coins.png';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useFormWithProvider } from 'forms';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import {
  EmployeeLoansCategoryFormData,
  UploadPolicyResponse,
  CreateLoanCategoryRequest,
  LoanCategory,
  UpdateLoanCategoryRequest,
  PolicySummaryItem,
} from '../types';
import {
  CREATE_LOAN_CATEGORY,
  LIST_LOAN_CATEGORIES,
  MAX_POLICY_FILE_SIZE,
  UPDATE_LOAN_CATEGORY,
  UPLOAD_LOAN_CATEGORY_POLICY,
} from '../constants';
import { useTheme } from '@razorpay/blade/components';
import CategorieFolderDark from '../../../../assets/loan/categorie-folder.png';
import CategorieFolderLight from '../../../../assets/loan/categorie-folder-light.png';

const PolicyDocumentStep = () => {
  const { colorScheme } = useTheme();
  const { show: showToast } = useToast();
  const isDark = colorScheme === 'dark';
  const { updateStepStatus, setCurrentStep, onSuccess } = useWizardInfo();
  const [openLearnMoreModal, setOpenLearnMoreModal] = useState(false);
  const { formData, setFieldValue } = useFormWithProvider<EmployeeLoansCategoryFormData>();
  const queryClient = useQueryClient();
  const policyFileId = formData.policyFileId;
  const policySummary = formData.policySummary ?? null;

  const uploadPolicyMutation = useMutation<UploadPolicyResponse, typeof AppError, File>({
    mutationKey: UPLOAD_LOAN_CATEGORY_POLICY,
    mutationFn: (file: File) => api.loans.uploadLoanCategoryPolicy(file),
    onSuccess: (response) => {
      setFieldValue('policyFileId', response.file_id);
      setFieldValue('policySummary', response.policy_summary);
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to upload policy document. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const downloadPolicyMutation = useMutation<{ signed_url: string }, typeof AppError, string>({
    mutationFn: (fileId: string) => api.loans.downloadLoanDocument(fileId),
    onSuccess: (res) => {
      window.open(res.signed_url, '_blank');
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to open document. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const hasExistingFile = !!policyFileId && (formData.policyDocumentFile ?? []).length === 0;

  const createCategoryMutation = useMutation<
    LoanCategory,
    typeof AppError,
    CreateLoanCategoryRequest
  >({
    mutationKey: CREATE_LOAN_CATEGORY,
    mutationFn: api.loans.createLoanCategory,
    onSuccess: () => {
      updateStepStatus(1, 'success');
      queryClient.invalidateQueries(LIST_LOAN_CATEGORIES);
      showToast({ content: 'Category created successfully', color: 'positive', duration: 4000 });
      onSuccess?.();
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to create category. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const updateCategoryMutation = useMutation<
    LoanCategory,
    typeof AppError,
    UpdateLoanCategoryRequest & { categoryId: number }
  >({
    mutationKey: UPDATE_LOAN_CATEGORY,
    mutationFn: (variables) => api.loans.updateLoanCategory(variables),
    onSuccess: () => {
      updateStepStatus(1, 'success');
      queryClient.invalidateQueries(LIST_LOAN_CATEGORIES);
      showToast({ content: 'Category updated successfully', color: 'positive', duration: 4000 });
      onSuccess?.();
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to update category. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const isEditMode = !!formData.editCategoryId;

  const handleSubmit = () => {
    if (isEditMode && formData.editCategoryId) {
      updateCategoryMutation.mutate({
        categoryId: formData.editCategoryId,
        name: formData.categoryName || '',
        is_critical: formData.isCategoryCritical,
        is_document_required: formData.isDocumentRequired,
        ...(policyFileId ? { policy_file_id: policyFileId, policy_summary: policySummary } : {}),
      });
    } else {
      createCategoryMutation.mutate({
        name: formData.categoryName || '',
        is_critical: formData.isCategoryCritical,
        is_document_required: formData.isDocumentRequired,
        ...(policyFileId ? { policy_file_id: policyFileId, policy_summary: policySummary } : {}),
      });
    }
  };

  const handleFileChange = ({ fileList }: { fileList: BladeFileList }) => {
    setFieldValue('policyDocumentFile', fileList);
    const file = fileList[0] as BladeFile;
    if (file) {
      uploadPolicyMutation.mutate(file);
    }
  };

  const handleFileRemove = ({ file }: { file: BladeFile }) => {
    const updatedFileList = (formData.policyDocumentFile ?? []).filter((f) => f.id !== file.id);
    setFieldValue('policyDocumentFile', updatedFileList);
    setFieldValue('policyFileId', undefined);
    setFieldValue('policySummary', null);
  };

  const handleFileDismiss = ({ file }: { file: BladeFile }) => {
    const updatedFileList = (formData.policyDocumentFile ?? []).filter((f) => f.id !== file.id);
    setFieldValue('policyDocumentFile', updatedFileList);
    setFieldValue('policyFileId', undefined);
    setFieldValue('policySummary', null);
  };

  return (
    <>
      <ModalBody height="100%" padding="spacing.0">
        <Box width="100%" height="100%" position="relative" overflow="hidden">
          <Box position="absolute" bottom="0px" right="0px" zIndex={0}>
            <img src={LoanCoins} alt="" width={196} height={196} />
          </Box>
          <Box height="100%" overflowY="auto" position="relative" zIndex={1}>
            <Box
              display="flex"
              flexDirection="column"
              gap="spacing.10"
              paddingY="spacing.9"
              maxWidth="462px"
              margin="auto">
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                padding="spacing.5"
                borderWidth="thin"
                borderColor="surface.border.gray.muted"
                backgroundColor="surface.background.gray.moderate"
                borderRadius="medium">
                <Box display="flex" flexDirection="column" gap="spacing.2">
                  <Text weight="semibold" color="surface.text.gray.normal">
                    {formData.categoryName}
                  </Text>
                  <Box display="flex" gap="spacing.2">
                    {formData.isCategoryCritical && (
                      <Text size="small" color="surface.text.gray.muted">
                        Critical Category
                      </Text>
                    )}
                    {formData.isDocumentRequired && (
                      <Text size="small" color="surface.text.gray.muted">
                        • Require Documentation from Employees
                      </Text>
                    )}
                  </Box>
                </Box>
                <IconButton
                  icon={EditIcon}
                  accessibilityLabel="Edit category details"
                  onClick={() => setCurrentStep(0)}
                  size="medium"
                />
              </Box>

              <Box display="flex" flexDirection="column" gap="spacing.5">
                <Box display="flex" flexDirection="column">
                  <Box display="flex" gap="spacing.3" alignItems="center">
                    <Heading size="large" weight="semibold">
                      Policy Details
                    </Heading>
                    <Badge
                      size="medium"
                      color={formData.isDocumentRequired ? 'negative' : 'neutral'}>
                      {formData.isDocumentRequired ? 'Required' : 'Optional'}
                    </Badge>
                  </Box>
                  <Text variant="body" size="medium" color="surface.text.gray.muted">
                    Upload a PDF guideline to help employees understand eligibility criteria and
                    policy rules.{' '}
                    <Link onClick={() => setOpenLearnMoreModal(true)}> Learn more </Link>
                  </Text>
                </Box>
                {hasExistingFile ? (
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Text size="small" weight="medium" color="surface.text.gray.muted">
                      Upload Policy Document{formData.isDocumentRequired ? '*' : ''}
                    </Text>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      padding="spacing.4"
                      borderWidth="thin"
                      borderRadius="medium"
                      borderColor="surface.border.gray.muted">
                      <Link
                        icon={DownloadIcon}
                        iconPosition="left"
                        size="small"
                        onClick={() => downloadPolicyMutation.mutate(policyFileId!)}
                        isDisabled={downloadPolicyMutation.isLoading}>
                        {downloadPolicyMutation.isLoading ? 'Loading…' : 'View Policy Document'}
                      </Link>
                      <Button
                        variant="secondary"
                        color="negative"
                        size="xsmall"
                        icon={TrashIcon}
                        onClick={() => {
                          setFieldValue('policyFileId', undefined);
                          setFieldValue('policySummary', null);
                        }}>
                        Remove
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <FileUpload
                    uploadType="single"
                    label="Upload Policy Document"
                    necessityIndicator={formData.isDocumentRequired ? 'required' : 'optional'}
                    helpText="PDF format only. Max 5MB per file. Only one document"
                    accept={MimeType.PDF}
                    maxSize={MAX_POLICY_FILE_SIZE}
                    fileList={formData.policyDocumentFile ?? []}
                    onChange={handleFileChange}
                    onRemove={handleFileRemove}
                    onDismiss={handleFileDismiss}
                    isDisabled={uploadPolicyMutation.isLoading}
                  />
                )}
              </Box>

              {policySummary && policySummary.length > 0 && (
                <Box
                  borderRadius="medium"
                  borderWidth="thinner"
                  borderColor="surface.border.gray.muted"
                  backgroundColor="surface.background.gray.intense">
                  <Accordion>
                    <AccordionItem>
                      <AccordionItemHeader
                        title="What employees will view when applying"
                        leading={<RayIcon color="interactive.icon.primary.normal" />}
                      />
                      <AccordionItemBody>
                        <Box display="flex" flexDirection="column">
                          {policySummary.map((item, idx) => (
                            <Box key={idx}>
                              <Box paddingY="spacing.4">
                                <Text size="small" color="surface.text.gray.subtle">
                                  {item.label}
                                </Text>
                                <Text
                                  size="small"
                                  color="surface.text.gray.muted"
                                  marginTop="spacing.1">
                                  {item.value}
                                </Text>
                              </Box>
                              {idx < policySummary.length - 1 && <Divider />}
                            </Box>
                          ))}
                        </Box>
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </ModalBody>

      <ModalFooter>
        <Box width="100%" display="flex" justifyContent="center" alignItems="center">
          <Box display="flex" justifyContent="space-between" width="462px">
            <Button variant="tertiary" onClick={() => setCurrentStep(0)}>
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={
                createCategoryMutation.isLoading ||
                updateCategoryMutation.isLoading ||
                uploadPolicyMutation.isLoading
              }
              isDisabled={
                (formData.isDocumentRequired || (formData.policyDocumentFile?.length ?? 0) > 0) &&
                !policyFileId
              }>
              {isEditMode ? 'Save Changes' : 'Create'}
            </Button>
          </Box>
        </Box>
      </ModalFooter>

      <Modal
        isOpen={openLearnMoreModal}
        onDismiss={() => setOpenLearnMoreModal(false)}
        size="medium">
        <ModalBody padding="spacing.0">
          <Box display="flex">
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              padding="spacing.7">
              <Box display="flex" flexDirection="column" alignItems="flex-start" gap="spacing.5">
                <Box>
                  <Text
                    size="large"
                    variant="body"
                    weight="medium"
                    color="surface.text.gray.normal">
                    Set It Once, Avoid Repeated Follow-Ups
                  </Text>
                  <Text
                    size="small"
                    variant="body"
                    weight="regular"
                    color="surface.text.gray.muted">
                    Share a loan policy to inform employees of the rules upfront
                  </Text>
                </Box>

                <Divider orientation="horizontal" width="100%" />
                <Box display="flex" flexDirection="column" gap="spacing.3">
                  <Text
                    size="small"
                    variant="body"
                    weight="regular"
                    color="surface.text.gray.subtle">
                    A policy outlines requestable amounts, eligibility, interest, and required
                    documents, ensuring employees submit complete requests upfront.
                  </Text>
                  <Text
                    size="small"
                    variant="body"
                    weight="regular"
                    color="surface.text.gray.subtle">
                    Spend less time answering questions and more time approving.
                  </Text>
                  <Text
                    size="small"
                    variant="body"
                    weight="regular"
                    color="surface.text.gray.subtle">
                    Not sure where to start?
                  </Text>
                  <Link
                    href="https://razorpay.com"
                    target="_blank"
                    icon={ExternalLinkIcon}
                    iconPosition="right"
                    size="medium">
                    See Example Policies
                  </Link>
                </Box>
              </Box>

              <Button isFullWidth variant="tertiary" onClick={() => setOpenLearnMoreModal(false)}>
                Close
              </Button>
            </Box>
            <Box
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="338px">
              <img
                src={isDark ? CategorieFolderDark : CategorieFolderLight}
                alt="Folder"
                width={338}
                height={150}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          </Box>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PolicyDocumentStep;
